import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Webcam from 'react-webcam';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { Icon } from '@components/atoms/icon';
import { Spinner } from '@components/atoms/spinner';
import { Typography } from '@components/atoms/typography';
import { CloseIcon } from '@components/icons/close-icon';
import { MaskIcon } from '@components/icons/mask-icon';
import {
    DocsVerificationBiometricTypeEnum,
    DocsVerificationLivenessEnum,
    DocsVerificationLivenessTagErrorEnum,
    OrganizationRoleEnum,
    VerificationStepEnum,
} from '@models/enums';
import type { FaceDetectResult, FileWithPreview, ThemeProps } from '@models/types';
import { FileErrorCodeEnum } from '@modules/dashboard/pages/docs-verification/docs-verification-page/types/file-error-code-enum';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/styles/useTheme';
import type { RootState } from '@store/root-reducer';
import * as faceDetection from '@tensorflow-models/face-detection';
import type { Face, FaceLandmarksDetector } from '@tensorflow-models/face-landmarks-detection';
import { base64ToFile, isAndroid, scrollTop, sleep } from '@utils/util';
import cn from 'classnames';
import { isMobile } from 'mobile-device-detect';
import { v4 as uuidv4 } from 'uuid';

import {
    FACE_DETECTOR_CONFIG,
    FACE_DETECTOR_LOADING_TIMEOUT,
} from '../../../../../docs-verification-steps/constants';

import { detectFace, MIN_FACE_SIZE_PERCENT } from './utils/detect-face';
import { Hints } from './hints';
import {
    FaceTemplateContainer,
    ProcessLivenessContainer,
    RequestLoadingContainer,
    WebCamContainer,
} from './styles';
import type { FacePositionsState, LivelessProps } from './types';
import { VerificationStatusEnum } from './types';

import '@tensorflow/tfjs-backend-webgl';

const androidMobileConstraints = {
    audio: false,
    video: {
        facingMode: 'user',
    },
};

const iOSMobileConstraints = {
    audio: false,
    video: {
        facingMode: 'user',
        width: {
            ideal: 768,
            min: 640,
        },
        height: {
            ideal: 1024,
            min: 480,
        },
    },
};

const desktopConstraints = {
    audio: false,
    video: {
        facingMode: 'user',
        width: {
            ideal: 1152,
            min: 640,
        },
        height: {
            ideal: 864,
            min: 480,
        },
    },
};

const defaultFacePositions = {
    [DocsVerificationLivenessEnum.CENTER]: false,
    [DocsVerificationLivenessEnum.UP]: false,
    [DocsVerificationLivenessEnum.RIGHT]: false,
    [DocsVerificationLivenessEnum.DOWN]: false,
    [DocsVerificationLivenessEnum.LEFT]: false,
};

const MIN_DETECT_COUNT = 3;
const ERROR_PAUSE_DELAY = 5000;

export const LivenessProcess: React.FC<
    LivelessProps & {
        onSubmit: (isFailedVerification?: boolean) => void;
        onClose: () => void;
    }
> = ({
    onCameraErrorClose,
    titleCameraNotAllowed,
    onFileChange,
    // checkStatus,
    onSubmit,
    onClose,
    className,
}) => {
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);

    const [cameraConstraints] = useState(() => {
        if (matchesMobileSm || isMobile) {
            if (isAndroid()) {
                return androidMobileConstraints;
            }

            return iOSMobileConstraints;
        }

        return desktopConstraints;
    });

    const containerRef = useRef<HTMLDivElement | null>(null);
    const webContainerRef = useRef<HTMLDivElement | null>(null);
    const faceDetectInterval = useRef<number | null>(null);
    const userRole = useSelector(
        (state: RootState) => state.auth?.user?.info?.global_role,
    ) as OrganizationRoleEnum;
    const { t } = useTranslation();
    const [isCameraNotAllowed, setIsCameraNotAllowed] = useState(false);
    const [currentStep, setCurrentStep] = useState<VerificationStepEnum>(
        VerificationStepEnum.CAMERA_ACCESS,
    );
    const webcamRef = useRef<Webcam>(null);
    const canvasRef = useRef<null | HTMLCanvasElement>(null);
    const isInitialized = useRef(false);
    const isDone = useRef(false);
    const faceDetectorFromStore = useSelector(
        (state: RootState) => state.docsVerification.faceDetector,
    );
    const faceDetector = useRef<FaceLandmarksDetector | null>(
        faceDetectorFromStore as FaceLandmarksDetector,
    );
    const facePositions = useRef<FacePositionsState>(defaultFacePositions);
    const failedChecksCount = useRef(0);
    const faceCount = useRef(1);
    const [status, setStatus] = useState<VerificationStatusEnum | null>(null);
    const isPaused = useRef(false);
    const isDetectInProgress = useRef(false);
    const detectCount = useRef(0);
    const [faceDetectResult, setFaceDetectResult] = useState<FaceDetectResult | null>(null);
    const maskRef = useRef<SVGSVGElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const capture = useCallback(
        async ({
            tag,
            detectResult,
            isIgnoreCheck,
            file,
        }: {
            documentType: DocsVerificationBiometricTypeEnum;
            tag?: DocsVerificationLivenessTagErrorEnum;
            detectResult?: FaceDetectResult;
            isIgnoreCheck: boolean;
            file?: FileWithPreview;
        }) => {
            if (webcamRef.current) {
                let fileWithPreview = file;

                if (!fileWithPreview) {
                    const imageSrc = webcamRef.current.getScreenshot(
                        matchesMobileSm && !isAndroid() && !theme.isLandscape
                            ? { width: 800, height: 600 }
                            : undefined,
                    );

                    fileWithPreview = (await base64ToFile(
                        imageSrc as string,
                        `photo_${uuidv4()}.jpeg`,
                    )) as FileWithPreview;

                    fileWithPreview = Object.assign(fileWithPreview, {
                        preview: imageSrc,
                    });
                }

                if (onFileChange) {
                    if (isIgnoreCheck) {
                        await onFileChange(fileWithPreview, tag, detectResult);
                    } else {
                        setStatus(VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS);
                        const [verificationFileState] = await Promise.all([
                            onFileChange(fileWithPreview, tag, detectResult),
                            sleep(1000),
                        ]);

                        if (verificationFileState.status.success) {
                            isPaused.current = false;
                            isDone.current = true;
                            onSubmit(verificationFileState.status.isFailedVerification);
                        } else {
                            if (
                                verificationFileState.status.message?.includes(
                                    String(FileErrorCodeEnum.POOR_LIGHTING_CONDITIONS),
                                )
                            ) {
                                isPaused.current = true;
                                setTimeout(() => {
                                    isPaused.current = false;
                                }, ERROR_PAUSE_DELAY);
                                detectCount.current = 0;
                                facePositions.current = defaultFacePositions;
                                setStatus(VerificationStatusEnum.POOR_LIGHTING);

                                return;
                            }
                            failedChecksCount.current += 1;
                            facePositions.current = defaultFacePositions;
                            setStatus(VerificationStatusEnum.ALIGN_FACE);
                            detectCount.current = 0;
                            isPaused.current = false;
                        }
                    }
                }
            }
        },
        [onFileChange, onSubmit, matchesMobileSm, theme.isLandscape],
    );

    const tryLoadFacemesh = useCallback(async (isSilent?: boolean) => {
        try {
            setStatus(
                isSilent
                    ? VerificationStatusEnum.FACE_DETECTOR_LOADING_SILENT
                    : VerificationStatusEnum.FACE_DETECTOR_LOADING,
            );

            const result = await Promise.race([
                faceDetection.createDetector(
                    faceDetection.SupportedModels.MediaPipeFaceDetector,
                    FACE_DETECTOR_CONFIG,
                ),
                sleep(FACE_DETECTOR_LOADING_TIMEOUT),
            ]);

            if (result) {
                faceDetector.current = result as FaceLandmarksDetector;
            } else {
                setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

                return false;
            }

            return true;
        } catch (e) {
            setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

            return false;
        }
    }, []);

    // Third step - make detections
    const detect = useCallback(async (): Promise<FaceDetectResult | undefined> => {
        if (
            typeof webcamRef.current !== 'undefined' &&
            webcamRef.current !== null &&
            webcamRef?.current?.video?.readyState === 4 &&
            faceDetector.current
        ) {
            const { video } = webcamRef.current;
            const { videoWidth } = webcamRef.current?.video;
            const { videoHeight } = webcamRef.current?.video;

            webcamRef.current.video.width = videoWidth || 720;
            webcamRef.current.video.height = videoHeight || 480;

            if (canvasRef.current) {
                canvasRef.current.width = videoWidth;
                canvasRef.current.height = videoHeight;

                let faces: Face[] = [];

                try {
                    faces = await faceDetector.current.estimateFaces(video);
                } catch (e) {
                    await tryLoadFacemesh(true);
                }

                detectCount.current += 1;

                // make a request with a warning that the person is not alone
                if (faces.length !== faceCount.current && faces.length > 1) {
                    faceCount.current = faces.length;
                    await capture({
                        documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO_EXTRA,
                        tag: DocsVerificationLivenessTagErrorEnum.MULTIPARTY,
                        isIgnoreCheck: true,
                    });
                }
                // Fourth step - get face coordinates and instructions for liveness

                if (faces[0]) {
                    return detectFace(
                        faces[0],
                        {
                            width: videoWidth,
                            height: videoHeight,
                        },
                        MIN_FACE_SIZE_PERCENT + failedChecksCount.current,
                    );
                }

                setStatus(VerificationStatusEnum.FACE_NOT_FOUND);
            }
        }

        return undefined;
    }, [capture, tryLoadFacemesh]);

    const checkFaceDetectedResult = useCallback(
        // eslint-disable-next-line complexity
        async (res: FaceDetectResult) => {
            if (Object.values(res)) {
                if (res.isFaceFound) {
                    setStatus(VerificationStatusEnum.FACE_FOUND);
                    if (res.isSuccess) {
                        isPaused.current = true;
                        await capture({
                            documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
                            detectResult: res,
                            isIgnoreCheck: false,
                        });
                    }
                } else {
                    setStatus(VerificationStatusEnum.FACE_NOT_FOUND);
                }
            }

            if (!isPaused.current) {
                setFaceDetectResult(res);
            }
        },
        [capture],
    );

    useEffect(() => {
        navigator.mediaDevices
            .getUserMedia(cameraConstraints)
            .then((stream) => {
                const tracks = stream.getTracks();

                tracks?.forEach((track) => {
                    track.stop();
                });
                setCurrentStep(VerificationStepEnum.SCANNING);
            })
            .catch(() => {
                setIsCameraNotAllowed(true);
            });
    }, [cameraConstraints]);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            const loadFaceMash = async () => {
                if (!faceDetector.current) {
                    const isLoaded = await tryLoadFacemesh();

                    if (!isLoaded) {
                        return;
                    }
                }

                setStatus(VerificationStatusEnum.READY);
                setTimeout(() => {
                    scrollTop();
                });
            };

            const startDetecting = async () => {
                if (isDone.current && faceDetectInterval.current) {
                    clearInterval(faceDetectInterval.current);

                    return;
                }

                if (isPaused.current || isDetectInProgress.current) {
                    return;
                }

                isDetectInProgress.current = true;
                const res = await detect();

                isDetectInProgress.current = false;

                if (res) {
                    await checkFaceDetectedResult(res);
                }
            };

            const initialize = async () => {
                await loadFaceMash();
                await sleep(1500);
                faceDetectInterval.current = setInterval(startDetecting, 1000) as unknown as number;
            };

            initialize();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkFaceDetectedResult, detect, tryLoadFacemesh]);

    const handleMakePhotoClick = useCallback(async () => {
        await capture({
            documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
            detectResult: faceDetectResult as FaceDetectResult,
            isIgnoreCheck: false,
        });
    }, [capture, faceDetectResult]);

    const handleUploadFileClick = useCallback(async () => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files) {
                isPaused.current = true;
                setStatus(VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS);
                const file = event.target.files[0] as FileWithPreview;

                file.preview = URL.createObjectURL(file);
                await capture({
                    documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
                    detectResult: faceDetectResult as FaceDetectResult,
                    isIgnoreCheck: false,
                    file,
                });
            }
        },
        [capture, faceDetectResult],
    );

    const getMaskStyle = useCallback(() => {
        if (theme.isLandscape) {
            return {
                height: webContainerRef.current?.clientHeight
                    ? Math.ceil((webContainerRef.current?.clientHeight - 50) / 10) * 10
                    : 'auto',
            };
        }
        if (matchesMobileSm) {
            return {
                height: webContainerRef.current?.clientHeight
                    ? Math.ceil((webContainerRef.current?.clientHeight - 200) / 10) * 10
                    : 'auto',
            };
        }

        return undefined;
    }, [matchesMobileSm, theme.isLandscape]);

    return (
        <ProcessLivenessContainer ref={containerRef} className={className}>
            {isCameraNotAllowed ? (
                <CameraAccessDefault title={titleCameraNotAllowed} onClose={onCameraErrorClose} />
            ) : (
                <React.Fragment>
                    {currentStep === VerificationStepEnum.CAMERA_ACCESS && (
                        <div className='spinner-container'>
                            <Spinner className='spinner' />
                        </div>
                    )}
                    {currentStep === VerificationStepEnum.SCANNING && (
                        <React.Fragment>
                            {!matchesMobileSm && (
                                <Typography
                                    tag='div'
                                    variant='h2'
                                    color='primary-a7'
                                    className='liveness-title'
                                >
                                    {t('docVerificationWizard.selfie.title')}
                                </Typography>
                            )}
                            <Hints
                                faceDetectResult={faceDetectResult}
                                verificationStatus={status}
                                tryLoadFacemesh={() => {
                                    tryLoadFacemesh();
                                }}
                                detectCount={detectCount.current}
                                minDetectCount={
                                    failedChecksCount.current >= 1 ? 0 : MIN_DETECT_COUNT
                                }
                            />
                            <WebCamContainer
                                ref={webContainerRef}
                                className={cn({
                                    default: !facePositions.current.center,
                                    center: status === VerificationStatusEnum.FACE_FOUND,
                                    error: status === VerificationStatusEnum.FACE_NOT_FOUND,
                                    loading:
                                        status ===
                                        VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS,
                                    transparent:
                                        failedChecksCount.current >= 1
                                            ? false
                                            : detectCount.current === 0,
                                })}
                            >
                                <Webcam
                                    mirrored={true}
                                    ref={webcamRef}
                                    audio={false}
                                    videoConstraints={cameraConstraints.video}
                                    screenshotFormat='image/jpeg'
                                    screenshotQuality={1}
                                    forceScreenshotSourceSize={true}
                                    minScreenshotHeight={2160}
                                    minScreenshotWidth={4096}
                                    width={matchesMobileSm ? window.innerWidth : 1920}
                                    height={matchesMobileSm ? window.innerHeight : 1080}
                                />
                                <canvas ref={canvasRef} />
                                <FaceTemplateContainer
                                    className={cn({
                                        loading:
                                            status ===
                                            VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS,
                                        [`mask-${webcamRef.current?.video?.width}`]:
                                            webcamRef.current?.video?.width,
                                    })}
                                >
                                    <div className='mask-wrapper'>
                                        <MaskIcon style={getMaskStyle()} ref={maskRef} />
                                    </div>
                                </FaceTemplateContainer>
                                {status === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS && (
                                    <RequestLoadingContainer>
                                        <Spinner className='spinner' />
                                    </RequestLoadingContainer>
                                )}
                            </WebCamContainer>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
            {!matchesMobileSm && !isCameraNotAllowed && (
                <Icon
                    icon={<CloseIcon />}
                    $clickable={true}
                    className='close-icon'
                    onClick={onClose}
                />
            )}
            <div className='liveness-process-actions'>
                {(process.env.REACT_APP_IS_SDK_DEBUG ||
                    userRole === OrganizationRoleEnum.ADMIN) && (
                    <div className='admin-actions'>
                        <Button
                            variant='primary'
                            className='take-photo-button'
                            onClick={handleMakePhotoClick}
                            size='xl'
                            loading={
                                status === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS ||
                                !faceDetectResult
                            }
                        >
                            {t('camera.takePhoto')}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type='file'
                            name='image'
                            accept='image/*'
                            multiple={false}
                            onChange={handleFileChange}
                            className='hidden'
                        />
                        <Button
                            variant='primary'
                            onClick={handleUploadFileClick}
                            size='xl'
                            loading={status === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS}
                        >
                            {t('common.uploadFile')}
                        </Button>
                    </div>
                )}
            </div>
        </ProcessLivenessContainer>
    );
};
